// frontend/src/App.js

import React, { useState, useEffect, useContext } from 'react';
import { useParams } from 'react-router-dom';

import {
  Route,
  Routes,
  useLocation,
  Link,
  Navigate,
  useNavigate,
} from 'react-router-dom';
import { colors } from './styles/designSystem';
import Login from './components/Login';
import {
  Menu,
  X,
  Phone,
  Book,
  Settings,
  List,
  PlaySquare,
  ShieldCheck,
  Brain,
  BarChart2,
  Calendar,
} from 'lucide-react';

import { GradientButton } from './components/ui/GradientButton';
import UnifiedConfiguration from './components/UnifiedConfiguration';
import CallDetails from './components/CallDetails';
import LiveTesting from './components/LiveTesting';
import CallLogs from './components/CallLogs';
import AdminPage from './components/AdminPage';
import ErrorBoundary from './components/ErrorBoundary';
import { useTranslation } from 'react-i18next';


// **Import AuthContext**
import { AuthContext } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import { setAuthHeaders } from './api';

import UsageOverview from './components/UsageOverview';
//import BillingOverview from './components/BillingOverview';

import CalendarView from './components/CalendarView';

// **Import Landing Page Components**
import Benefits from './components/landing/Benefits';
import FAQ from './components/landing/FAQ';
import LandingHero from './components/landing/Hero';
import KeyFeatures from './components/landing/KeyFeatures';
import UseCaseScenarios from './components/landing/UseCaseScenarios';
import Footer from './components/landing/Footer';
import Examples from './components/landing/Examples';
import HowTo from './components/landing/HowTo';
import PrivacyPolicy from './components/landing/PrivacyPolicy';
import TermsOfService from './components/landing/TermsOfService';
import CalendarCallback from './components/CalendarCallback';
import SeoHead from './components/SeoHead';
import EditorialPage from './components/landing/EditorialPage';
import NotFound from './components/NotFound';


import { logPageView } from './ga'; 

import ReactGA from 'react-ga4';


const LandingPage = () => {
  const { t, i18n } = useTranslation();
  const lang = ['fr', 'en', 'de'].includes(i18n.language) ? i18n.language : 'en';
  const faqKeys = ['handling_complex_inquiries', 'supported_languages', 'getting_started', 'data_security_privacy', 'billing'];
  const faqItems = faqKeys.map((key) => ({
    question: t(`faq.questions.${key}.question`),
    answer: t(`faq.questions.${key}.answer`),
  }));
  return (
    <div>
      <SeoHead lang={lang} faqItems={faqItems} />
      <div id="hero"><LandingHero /></div>
      <div id="usecases"><UseCaseScenarios /></div>
      <div id="examples"><Examples /></div>
      <div id="benefits"><Benefits /></div>
      <div id="how-to"><HowTo /></div>
      <div id="features"><KeyFeatures /></div>
      <div id="faq"><FAQ /></div>
    </div>
  );
};

// Route change tracking component
// src/App.js
const RouteChangeTracker = () => {
  const location = useLocation();
  
  useEffect(() => {
    const page = location.pathname + location.search;
    ReactGA.send({ 
      hitType: "pageview", 
      page,
      title: document.title 
    });
  }, [location]);

  return null;
};

const PrivateRouteSeo = () => {
  const { pathname } = useLocation();
  const privatePrefixes = ['/login', '/configuration', '/call-logs', '/call-details', '/live-testing', '/usage', '/admin', '/calendar', '/calendar-callback', '/onboarding'];
  return privatePrefixes.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`))
    ? <SeoHead noindex title="Sonitrix" description="Private Sonitrix application page." />
    : null;
};


// Navigation component
const Navigation = () => {
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const { user, logout } = useContext(AuthContext);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Keep your existing authenticatedNavigationItems
  const authenticatedNavigationItems = [
    {
      name: t('App.navigation.configuration'),
      href: '/configuration',
      icon: Settings,
      children: [
        { name: t('App.navigation.businessProfile'), href: '/configuration/profile', icon: ShieldCheck },
        { name: t('App.navigation.knowledgeBase'), href: '/configuration/knowledge-base', icon: Book },
        { name: t('App.navigation.promptSettings'), href: '/configuration/prompt', icon: Brain },
        { name: t('App.navigation.systemSettings'), href: '/configuration/settings', icon: Settings },
      ],
    },
    { name: t('App.navigation.callLogs'), href: '/call-logs', icon: Phone },
    { name: t('App.navigation.liveTesting'), href: '/live-testing', icon: PlaySquare },
    { name: t('App.navigation.usage'), href: '/usage', icon: BarChart2 },
    { name: t('App.navigation.admin'), href: '/admin', icon: List },
    { name: t('App.navigation.calendar'), href: '/calendar', icon: Calendar },
  ];

  const publicNavigationItems = [
    { name: t('navigation.items.home'), href: '#hero' },
    { name: t('navigation.items.useCaseScenarios'), href: '#usecases' },
    { name: t('navigation.items.benefits'), href: '#benefits' },
    { name: t('navigation.items.howto'), href: '#how-to' },
    { name: t('navigation.items.features'), href: '#features' },
    { name: t('navigation.items.faq'), href: '#faq' },
  ];

  const navigationItems = user ? authenticatedNavigationItems : publicNavigationItems;

  const handleSmoothScroll = (e, href) => {
    if (!user && href.startsWith('#')) {
      e.preventDefault();
      const element = document.querySelector(href);
      if (element) {
        const offset = 80; // Height of your navbar
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
        setIsMenuOpen(false);
      }
    }
  };

  const renderNavigationItem = (item, isMobile = false) => {
    const Icon = item.icon;
    const isActive = item.children
      ? item.children.some((child) => location.pathname === child.href)
      : location.pathname === item.href;

    if (user) {
      return (
        <Link
          key={item.name}
          to={item.href}
          className={`flex items-center ${colors.text.body} hover:text-violet-600
            transition-colors text-sm font-medium
            ${isActive ? 'text-violet-600' : ''}
            ${isMobile ? 'w-full px-4 py-2 hover:bg-gray-50 rounded-lg' : ''}`}
          onClick={() => isMobile && setIsMenuOpen(false)}
        >
          {Icon && <Icon className="w-4 h-4 mr-2" />}
          {item.name}
        </Link>
      );
    } else {
      return (
        <a
          key={item.name}
          href={item.href}
          onClick={(e) => handleSmoothScroll(e, item.href)}
          className={`flex items-center ${colors.text.body} hover:text-violet-600
            transition-colors text-sm font-medium
            ${isMobile ? 'w-full px-4 py-2 hover:bg-gray-50 rounded-lg' : ''}`}
        >
          {item.name}
        </a>
      );
    }
  };

  return (
    <nav
      className={`fixed w-full z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-white/80 backdrop-blur-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link
            to="/"
            className={`${colors.text.heading} ${colors.primary.text} text-xl font-bold`}
          >            SONITRIX
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            {navigationItems.map((item) => renderNavigationItem(item))}
            
            {user ? (
              <>
                <span className={`${colors.text.body} text-sm font-medium`}>
                  {user.name}
                </span>
                <GradientButton onClick={logout}>
                  Logout
                </GradientButton>
              </>
            ) : (
              <GradientButton to="/login">
                {t('App.navigation.getStarted')}
              </GradientButton>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            className="lg:hidden p-2 rounded-md text-gray-600 hover:text-violet-600 transition-colors"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden absolute top-full left-0 w-full bg-white shadow-lg border-t border-gray-100">
            <div className="px-4 py-2 space-y-2">
              {navigationItems.map((item) => (
                <React.Fragment key={item.name}>
                  {renderNavigationItem(item, true)}
                  {item.children && user && (
                    <div className="pl-8 space-y-2">
                      {item.children.map((child) => renderNavigationItem(child, true))}
                    </div>
                  )}
                </React.Fragment>
              ))}
              
              <div className="p-4">
                {user ? (
                  <>
                    <span className={`${colors.text.body} text-sm font-medium`}>
                      {user.name}
                    </span>
                    <GradientButton onClick={logout} className="w-full mt-2">
                      Logout
                    </GradientButton>
                  </>
                ) : (
                  <GradientButton to="/login" className="w-full">
                    {t('App.navigation.getStarted')}
                  </GradientButton>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// **Redirect component to handle root path based on authentication**
const RootRedirect = () => {
  const { lang } = useParams();
  const { i18n } = useTranslation();

  useEffect(() => {
    if (lang && ['en', 'fr', 'de'].includes(lang)) {
      i18n.changeLanguage(lang);
    } else {
      const storedLang = localStorage.getItem('i18nextLng') || 'en';
      i18n.changeLanguage(storedLang);
    }
  }, [lang, i18n]);
  const { user } = useContext(AuthContext);
  return user ? <Navigate to="/live-testing" replace /> : <LandingPage />;
};

const PUBLIC_SLUGS = [
  'agent-vocal-ia',
  'standard-telephonique-ia',
  'prise-rendez-vous-ia',
  'assistant-ia-artisans',
  'assistant-ia-avocats',
  'assistant-ia-comptables',
  'tarifs',
  'integrations',
  'securite-rgpd',
  'etudes-de-cas',
  'guides',
];

const PUBLIC_LANGUAGES = ['en', 'fr', 'de'];

const needsTrailingSlash = (pathname) => {
  const parts = pathname.split('/').filter(Boolean);
  if (!parts.length || pathname.endsWith('/')) {
    return false;
  }

  const [lang, slug] = parts;
  return PUBLIC_LANGUAGES.includes(lang) && (parts.length === 1 || (parts.length === 2 && PUBLIC_SLUGS.includes(slug)));
};

const App = () => {
  const { i18n } = useTranslation(); // Initialize the translation functions
  const { user } = useContext(AuthContext); // Access user from AuthContext
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    logPageView();
  }, [location]);

  useEffect(() => {
    if (needsTrailingSlash(location.pathname)) {
      navigate(`${location.pathname}/${location.search}${location.hash}`, { replace: true });
    }
  }, [location.hash, location.pathname, location.search, navigate]);

  useEffect(() => {
    if (user) {
      setAuthHeaders(user.token, user.tenantId);
    } else {
      setAuthHeaders(null, null);
    }
  }, [user]);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    localStorage.setItem('i18nextLng', lng); // Store language preference
    // Update the URL to include the language code
    const newPath = location.pathname.replace(/^\/(en|fr|de)/, `/${lng}`);
    const normalizedPath = needsTrailingSlash(newPath) ? `${newPath}/` : newPath;
    navigate(normalizedPath, { replace: true });
  };

  useEffect(() => {
    // Get language from URL path
    const pathLang = location.pathname.split('/')[1];
    const storedLang = localStorage.getItem('i18nextLng');
    
    if (pathLang && ['en', 'fr', 'de'].includes(pathLang)) {
        i18n.changeLanguage(pathLang);
    } else if (location.pathname === '/' && storedLang && ['en', 'fr', 'de'].includes(storedLang)) {
        i18n.changeLanguage(storedLang);
        navigate(`/${storedLang}/`, { replace: true });
    } else if (location.pathname === '/') {
        // Default to English if no language is detected
        i18n.changeLanguage('en');
        navigate('/en/', { replace: true });
    }
}, [i18n, location.pathname, navigate]);

  useEffect(() => {
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);
  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 relative">
        <Navigation />
        <RouteChangeTracker />
        <PrivateRouteSeo />

        {/* Language Switcher */}
        <div className="fixed bottom-4 right-4 flex space-x-2 z-50">
        <button
        onClick={() => changeLanguage('en')}
        className="px-3 py-1 bg-gray-200 rounded shadow-lg hover:bg-gray-300"
        >
        EN
        </button>
        <button
        onClick={() => changeLanguage('fr')}
        className="px-3 py-1 bg-gray-200 rounded shadow-lg hover:bg-gray-300"
        >
        FR
        </button>
        <button
        onClick={() => changeLanguage('de')}
        className="px-3 py-1 bg-gray-200 rounded shadow-lg hover:bg-gray-300"
        >
        DE
        </button>
        </div>

        {/* Main Content */}
        <div className="pt-16">
          <Routes>
          <Route path="/calendar-callback" element={<CalendarCallback />} />

          <Route path="/:lang" element={<RootRedirect />} />
          {PUBLIC_SLUGS.map((slug) => (
            <Route key={slug} path={`/:lang/${slug}`} element={<EditorialPage slug={slug} />} />
          ))}

          <Route path="/howto" element={<Navigate to="/en/" replace />} />
          <Route 
            path="/privacy-policy" 
            element={
              <div className="pt-16">
                <SeoHead lang="en" title="Privacy Policy | Sonitrix" description="Read the Sonitrix privacy policy and learn how personal information is collected, used and protected." canonicalUrl="https://www.sonitrix.com/privacy-policy" alternates={false} />
                <PrivacyPolicy />
              </div>
            } 
          />
          <Route 
            path="/terms-of-service" 
            element={
              <div className="pt-16">
                <SeoHead lang="en" title="Terms of Service | Sonitrix" description="Read the terms governing access to and use of Sonitrix services." canonicalUrl="https://www.sonitrix.com/terms-of-service" alternates={false} />
                <TermsOfService />
              </div>
            } 
          />

        <Route 
        path="/" 
        element={
            <Navigate 
                to={`/${localStorage.getItem('i18nextLng') || 'en'}/`} 
                replace 
              />
             } 
          />


            {/* Protected Routes */}
            <>
            <Route
            path="/configuration/*"
            element={
            <ProtectedRoute>
              <UnifiedConfiguration />
            </ProtectedRoute>
            }
            />
            <Route
            path="/call-logs"
            element={
            <ProtectedRoute>
              <CallLogs />
            </ProtectedRoute>
            }
            />
            <Route
            path="/call-details/:callId"
            element={
            <ProtectedRoute>
              <CallDetails />
            </ProtectedRoute>
            }
            />
            <Route
            path="/live-testing"
            element={
            <ProtectedRoute>
              <LiveTesting />
            </ProtectedRoute>
            }
            />
            <Route
            path="/usage"
            element={
            <ProtectedRoute>
              <UsageOverview />
            </ProtectedRoute>
            }
            />
            <Route
            path="/admin"
            element={
            <ProtectedRoute>
              <AdminPage />
            </ProtectedRoute>
            }
            />
            <Route
            path="/calendar"
            element={
            <ProtectedRoute>
              <CalendarView />
            </ProtectedRoute>
            }
            />
            </>

            {/* Authentication Route */}
            <Route
            path="/login"
            element={user ? <Navigate to="/" replace /> : <><SeoHead noindex title="Login | Sonitrix" description="Secure Sonitrix account login." /><Login /></>}
            />

            {/* Fallback Route */}
            <Route path="/:lang/*" element={<NotFound />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
          <Footer /> {/* Add this line */}

        {/* **Optionally, render the GoogleLoginButton directly if not using a separate login page** */}
        {/* <GoogleLoginButton /> */}
        </div>
        </div>
    </ErrorBoundary>

  );
};

export default App;
